import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar,
  Menu, MenuItem, Divider, useMediaQuery, useTheme, Chip, BottomNavigation,
  BottomNavigationAction, Paper
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  SettingsInputComponent as StationIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  BarChart as AnalyticsIcon,
  ManageAccounts as UsersIcon,
  ChevronLeft as ChevronLeftIcon,
  Shield as ShieldIcon,
  Security as AuditIcon,
} from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace' }}>
          <h2 style={{ color: '#dc2626' }}>Page Error</h2>
          <pre style={{ background: '#fee2e2', padding: 16, borderRadius: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {this.state.error?.toString()}{'\n\n'}{this.state.error?.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const BLUE = '#00A8E8';
const GREEN = '#86E000';
const DARK_BLUE = '#005B8F';
const DRAWER_WIDTH = 270;

const menuItems = [
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  { text: 'Locations', icon: <LocationIcon />, path: '/locations' },
  { text: 'Station Config', icon: <StationIcon />, path: '/stations' },
  { text: 'Patients', icon: <PeopleIcon />, path: '/patients' },
  { text: 'Register Patient', icon: <PersonAddIcon />, path: '/register' },
  { text: 'User Management', icon: <UsersIcon />, path: '/users', role: 'superadmin' },
  { text: 'Audit Log', icon: <AuditIcon />, path: '/audit-logs', role: 'superadmin' },
];

function Layout({ children, onLogout, user }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const visibleMenu = menuItems.filter(item => !item.role || user?.role === item.role || user?.role === 'superadmin');
  const currentPathIndex = visibleMenu.findIndex(i => i.path === location.pathname);
  const username = user?.name || user?.email || 'Admin';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
      {/* Eros Wellness Logo Header */}
      <Box sx={{
        px: 2.5, py: 2, display: 'flex', alignItems: 'center',
        bgcolor: '#fff',
        borderBottom: `3px solid transparent`,
        borderImage: `linear-gradient(90deg, ${BLUE}, ${GREEN}) 1`,
        position: 'relative',
      }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <img
            src="/eros-wellness-logo.png"
            alt="Eros Wellness AI Clinic"
            style={{ height: 72, width: 'auto', objectFit: 'contain', maxWidth: '100%' }}
          />
        </Box>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: BLUE, ml: 1 }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1.5, py: 2 }}>
        {visibleMenu.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                sx={{
                  borderRadius: '12px', py: 1.3, px: 2,
                  background: isActive
                    ? `linear-gradient(135deg, rgba(0,168,232,0.1), rgba(134,224,0,0.08))`
                    : 'transparent',
                  color: isActive ? BLUE : '#64748b',
                  '&:hover': {
                    background: isActive
                      ? `linear-gradient(135deg, rgba(0,168,232,0.14), rgba(134,224,0,0.1))`
                      : 'rgba(0,168,232,0.04)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: isActive ? BLUE : '#94a3b8', transition: 'color 0.2s' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: isActive ? 600 : 500, fontSize: '0.9rem' }}
                />
                {isActive && (
                  <Box sx={{
                    width: 4, height: 24, borderRadius: 2,
                    background: `linear-gradient(180deg, ${BLUE}, ${GREEN})`,
                  }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Role badge + version */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {user?.role === 'superadmin' && (
          <Chip icon={<ShieldIcon />} label="Super Admin" size="small"
            sx={{ fontSize: '0.7rem', bgcolor: '#f3e8ff', color: '#7c3aed', border: 'none' }} />
        )}
        <Chip label="v2.1.0" size="small" variant="outlined"
          sx={{ fontSize: '0.7rem', borderColor: `${BLUE}44`, color: BLUE, ml: 'auto' }} />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f7fa' }}>
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH, flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH, border: 'none',
              boxShadow: '2px 0 20px rgba(8,145,178,0.06)',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Sidebar - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 'none' } }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main Content */}
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
        pb: isSmall ? '64px' : 0,
      }}>
        {/* Top Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(8,145,178,0.08)',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {isMobile && !isSmall && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1, color: BLUE }}>
                <MenuIcon />
              </IconButton>
            )}
            {isSmall && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1, color: BLUE }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{
                color: '#0f172a', fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.15rem' },
              }}>
                {visibleMenu.find(i => i.path === location.pathname)?.text || 'Eros Wellness'}
              </Typography>
            </Box>

            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer', px: 1.5, py: 0.5, borderRadius: '12px',
                '&:hover': { bgcolor: 'rgba(8,145,178,0.06)' },
                transition: 'all 0.2s ease',
              }}
            >
              <Avatar sx={{
                width: 36, height: 36,
                background: `linear-gradient(135deg, ${DARK_BLUE} 0%, ${BLUE} 100%)`,
                fontSize: '0.85rem', fontWeight: 700,
                boxShadow: `0 2px 8px rgba(0,168,232,0.35)`,
              }}>
                {(username || 'A')[0].toUpperCase()}
              </Avatar>
              <Typography variant="body2" sx={{
                fontWeight: 600, color: '#0f172a',
                display: { xs: 'none', sm: 'block' },
              }}>
                {username || 'Admin'}
              </Typography>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { mt: 1, borderRadius: 3, minWidth: 180 } }}
            >
              <MenuItem disabled>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Signed in as <strong>{username}</strong>
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setAnchorEl(null); onLogout(); }}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box sx={{
          flex: 1, p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'auto',
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(6,182,212,0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14,165,233,0.03) 0%, transparent 50%)
          `,
        }}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </Box>
      </Box>

      {/* Bottom Navigation - Mobile only (small screens) */}
      {isSmall && (
        <Paper
          sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
            borderTop: '1px solid rgba(8,145,178,0.1)',
          }}
          elevation={8}
        >
          <BottomNavigation
            value={currentPathIndex >= 0 ? currentPathIndex : 0}
            onChange={(_, newValue) => navigate(visibleMenu[newValue].path)}
            showLabels
            sx={{
              height: 64,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                px: 0.5,
                color: '#94a3b8',
                '&.Mui-selected': { color: BLUE },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
                '&.Mui-selected': { fontSize: '0.68rem', fontWeight: 600 },
              },
            }}
          >
            {visibleMenu.slice(0, 5).map((item) => (
              <BottomNavigationAction
                key={item.text}
                label={item.text.split(' ')[0]}
                icon={item.icon}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}

export default Layout;
